import React, { useEffect, useState } from 'react';
import {
  CircularProgress, Alert, Grid, Card, CardContent, Typography, Divider,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { ReportLayout, useReportDates } from './ReportLayout';
import { reportsApi } from '../../api/reports';

const defectColumns: GridColDef[] = [
  { field: 'name', headerName: 'Defect Type', flex: 1, minWidth: 150 },
  { field: 'category', headerName: 'Category', width: 120 },
  { field: 'count', headerName: 'Incidents', type: 'number', width: 100 },
  { field: 'quantity', headerName: 'Total Pieces', type: 'number', width: 120 },
];

function StatCard({ label, value, unit }: { label: string; value: number | string; unit?: string }) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h5">
          {value}{unit && <Typography variant="caption" sx={{ ml: 0.5 }}>{unit}</Typography>}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function QualityControlReport() {
  const { startDate, endDate, setStartDate, setEndDate } = useReportDates();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await reportsApi.getQualityControl({
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      });
      setData(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const fqc = data?.summary?.finishing_qc || {};
  const sqc = data?.summary?.stitching_qc || {};

  return (
    <ReportLayout
      title="Quality Control Report"
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={setStartDate}
      onEndDateChange={setEndDate}
      loading={loading}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <CircularProgress />
      ) : data ? (
        <>
          <Typography variant="subtitle1" gutterBottom>Finishing QC</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}><StatCard label="Processed" value={fqc.total_processed || 0} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Passed" value={fqc.total_passed || 0} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Rejected" value={fqc.total_rejected || 0} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="First Pass Yield" value={fqc.first_pass_yield || 0} unit="%" /></Grid>
          </Grid>

          <Typography variant="subtitle1" gutterBottom>Stitching QC</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}><StatCard label="Total Received" value={sqc.total_received || 0} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Approved" value={sqc.approved || 0} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Refinish" value={sqc.refinish || 0} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Rejected" value={sqc.rejected || 0} /></Grid>
          </Grid>

          <StatCard label="Pending Repairs" value={data.summary?.pending_repairs || 0} />

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>Defects by Type</Typography>
          <DataGrid
            rows={data.defect_types || []}
            columns={defectColumns}
            getRowId={(row) => row.name}
            autoHeight
            disableRowSelectionOnClick
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            pageSizeOptions={[10, 25]}
          />
        </>
      ) : null}
    </ReportLayout>
  );
}
